/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon, Form, FormHandlers, Grid, ICON_CLOSE, Message,
} from '@doveit/bricks';
import AgentAutoComplete from '../../../containers/agent-autocomplete/AgentAutoComplete';
import { AgentStatus, ReferenceType } from '../../../domain/types';
import { Agency, Agent } from '../../../providers/api/dtos';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { toAgentPreviewProps } from '../../../agent/mappers/toAgentPreviewProps';
import { goToDetailPage } from '../../../utils/navigate/utils';
import * as styles from './AgencyCoordinatorsForm.style';

export interface AgencyCoordinatorsFormModel {
  coordinators: Agent[],
}

export interface AgencyCoordinatorsFormProps extends React.AriaAttributes {
  agency: Agency,
  onSubmit: (data: AgencyCoordinatorsFormModel) => void,
  initialValues?: AgencyCoordinatorsFormModel,
  innerRef?: React.MutableRefObject<FormHandlers>,
  loading?: boolean,
}

const AGENCY_COORDINATORS_FORM_DEFAULT_VALUES: AgencyCoordinatorsFormModel = {
  coordinators: [],
};

const AgencyCoordinatorsForm: React.FC<AgencyCoordinatorsFormProps> = ({
  agency,
  initialValues,
  innerRef,
  onSubmit,
  loading = false,
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...AGENCY_COORDINATORS_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  const goToAgentPage = React.useCallback((agentId: NonNullable<Agent['id']>) => () => {
    goToDetailPage(agentId, ReferenceType.AGENT);
  }, []);

  return (
    <Form
      initialValues={mergedInitialValues}
      onSubmit={onSubmit}
      innerRef={innerRef}
      loading={loading}
    >
      {({ values }) => (
        <Form.FieldArray
          name="coordinators"
          render={({ push, remove }) => (
            <>
              <Form.Item size={{ MD: 1 / 2 }}>
                <AgentAutoComplete
                  clearValueOnSelection
                  excludeAgentsByEmail={values.coordinators.map(({ email }) => email.work || email.personal || '')}
                  label="Aggiungi coordinatore"
                  aria-label="Campo per aggiungere un coordinatore dell'agenzia"
                  placeholder="Cerca per nome"
                  status={AgentStatus.ACTIVE}
                  agencyId={agency.id!}
                  onSuggestionSelected={(agent) => {
                    if (values.coordinators.find(({ id }) => id === agent?.id)) {
                      return;
                    }

                    push(agent);
                  }}
                />
              </Form.Item>
              <Form.Fieldset legend="Coordinatori aggiunti">
                <Form.Item>
                  {values.coordinators.length === 0 && (
                    <Message message="Nessun coordinatore selezionato." />
                  )}
                  {values.coordinators.length > 0 && (
                    <Grid gutter={75}>
                      {values.coordinators.map((coordinator) => (
                        <Grid.Unit
                          key={coordinator.id}
                          size={{ MD: 1 / 2 }}
                        >
                          <div css={styles.agentSelectionWrapper}>
                            <ViewAgentSmallPreview
                              {...toAgentPreviewProps(coordinator)}
                              aria-label="Anteprima coordinatore"
                              onClick={goToAgentPage(coordinator.id!)}
                            />
                            <div css={styles.agentRemoveAction}>
                              <ActionIcon
                                label="Rimuovi coordinatore"
                                aria-label="Azione per rimuovere il coordinatore"
                                onClick={() => remove(values.coordinators.findIndex((a) => a.id === coordinator.id))}
                                size="XS"
                                icon={{ path: ICON_CLOSE }}
                              />
                            </div>
                          </div>
                        </Grid.Unit>
                      ))}
                    </Grid>
                  )}
                </Form.Item>
              </Form.Fieldset>
            </>
          )}
        />
      )}
    </Form>
  );
};

export default AgencyCoordinatorsForm;
